import { useEffect, useCallback, useMemo, useRef } from 'react';
import { toLonLat } from 'ol/proj';
import { useOpenLayersContext } from '@ais-ui/react-openlayers';
import {
  convertToDDMM,
  convertToDegrees,
  convertToDMS,
  CursorPositionFormat,
} from '@ais-ui/utils';
import { useSettings } from '../Settings/SettingsProvider';
import { useAISMapContext } from './AISMapContext';
import { throttle } from 'lodash';

export function useCursorCoordinates() {
  const { map } = useOpenLayersContext();
  const { settings } = useSettings();
  const { cursorPosition, setCursorPosition } = useAISMapContext();
  const isDragging = useRef(false);

  const convertCoordinates = useMemo(() => {
    const format = settings['ribbonPlugin.settings']?.cursor_position_format;

    return (coords: number[]) => {
      let lat, lon;

      switch (format) {
        case CursorPositionFormat.positionDegrees:
          lat = convertToDegrees(coords[1], 'lat');
          lon = convertToDegrees(coords[0], 'lon');
          break;
        case CursorPositionFormat.positionDMS:
          lat = convertToDMS(coords[1], 'lat');
          lon = convertToDMS(coords[0], 'lon');
          break;
        default:
          lat = convertToDDMM(coords[1], 'lat');
          lon = convertToDDMM(coords[0], 'lon');
      }

      return { lat, lon };
    };
  }, [settings['ribbonPlugin.settings']?.cursor_position_format]);

  // Handle drag states
  useEffect(() => {
    if (!map) return;

    const handleMoveStart = () => {
      isDragging.current = true;
    };

    const handleMoveEnd = () => {
      isDragging.current = false;
    };

    map.on('movestart', handleMoveStart);
    map.on('moveend', handleMoveEnd);

    return () => {
      map.un('movestart', handleMoveStart);
      map.un('moveend', handleMoveEnd);
    };
  }, [map]);

  // Create a throttled handler that checks for dragging
  const handleMouseMove = useCallback(
    throttle((event: MouseEvent) => {
      if (settings['ribbonPlugin.settings']?.show_cursor_position !== true)
        return;
      if (isDragging.current) return; // Skip updates while dragging, as they cause re-renders of map overlay

      if (!map) return;

      // Get the map's target element (the container)
      const mapTarget = map.getTarget();
      if (!mapTarget) return;

      // Get the element (handle both string ID and HTMLElement cases)
      const mapElement =
        typeof mapTarget === 'string'
          ? document.getElementById(mapTarget)
          : mapTarget;

      if (!mapElement) return;

      // Get the bounding rectangle of the map container
      const rect = mapElement.getBoundingClientRect();

      // Calculate pixel coordinates relative to the map container
      const pixelCoords = [event.clientX - rect.left, event.clientY - rect.top];

      // Get map coordinates from the pixel position
      const mapCoordinates = map.getCoordinateFromPixel(pixelCoords);

      if (!mapCoordinates) return;

      // Convert from map projection (EPSG:3857) to lon/lat (EPSG:4326)
      const coordinates = toLonLat(mapCoordinates);

      if (!coordinates) return;

      setCursorPosition(convertCoordinates(coordinates));
    }, 50),
    [map, convertCoordinates, setCursorPosition]
  );

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      handleMouseMove.cancel();
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [handleMouseMove]);

  return cursorPosition;
}
